import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { USER_API_BASE } from '../api/config';
import { getToken, setToken, clearToken } from '../api/tokenStore';
import {
  userApi,
  apiFetch,
  ApiError,
  onUnauthorized,
  CHANGE_PASSWORD_PATH,
} from '../api/http';
import { logError } from '../utils/errorHandling';
import type { components } from '../api/types.gen';

// The current authenticated user, exactly as GET /api/users/me returns it.
type MeResponse = components['schemas']['MeResponse'];

// Backend Role enum values (STUDENT|TEACHER|ADMIN|SUPER_ADMIN|SERVICE).
type BackendRole = MeResponse['role'];

// Lowercase role strings the router/UI expect. SERVICE has no UI-facing role.
type UserRole = 'student' | 'teacher' | 'admin' | 'developer';

// better-auth's sign-in/email and change-password responses carry the new
// session token directly on the body (`{ token, user, ... }`), which the
// generated OpenAPI types don't model — hence these narrow local shapes.
interface SignInResponse {
  token?: string;
  user?: unknown;
}

interface ChangePasswordResponse {
  token?: string;
}

// better-auth's get-session response nests the session (and its token) under
// `session`; not modeled by the generated types, so read it via a narrow shape.
interface GetSessionResponse {
  session?: {
    token?: string;
  } | null;
}

/**
 * Value exposed by AuthContext. Consumed via the useAuth hook, which throws
 * when read outside an AuthProvider (so consumers never see undefined).
 */
export interface AuthContextValue {
  currentUser: MeResponse | null;
  userRole: UserRole | null;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<boolean>;
  updatePassword: (
    newPassword: string,
    currentPassword?: string | null
  ) => Promise<boolean>;
  logout: () => Promise<void>;
  loading: boolean;
  authStatus: string;
  authError: string | null;
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined
);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Backend Role enum (STUDENT|TEACHER|ADMIN|SUPER_ADMIN|SERVICE) -> lowercase
// role strings the router/UI expect. SERVICE has no UI-facing role.
const ROLE_MAP: Partial<Record<BackendRole, UserRole>> = {
  STUDENT: 'student',
  TEACHER: 'teacher',
  ADMIN: 'admin',
  SUPER_ADMIN: 'developer',
};

const BOOTSTRAP_TIMEOUT_MS = 15000;
class BootstrapTimeoutError extends Error {}

const mapRole = (backendRole: BackendRole): UserRole | null =>
  ROLE_MAP[backendRole] || null;

export async function refreshTokenFromSession(
  shouldApply: () => boolean = () => true
): Promise<GetSessionResponse> {
  const session = await apiFetch<GetSessionResponse>(
    USER_API_BASE,
    '/api/auth/get-session'
  );
  if (session?.session?.token && shouldApply()) {
    setToken(session.session.token);
  }
  return session;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({
  children,
}: AuthProviderProps): React.JSX.Element => {
  const [currentUser, setCurrentUser] = useState<MeResponse | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [authStatus, setAuthStatus] = useState(
    'Initializing authentication...'
  );
  const [authError, setAuthError] = useState<string | null>(null);
  const bootstrapTimedOutRef = useRef(false);

  // Fetch /api/users/me and apply the mapped role. On an unmappable role,
  // clear auth state entirely so we never leave currentUser populated
  // while userRole is null (that combination reads as "still loading").
  const loadCurrentUser = async (
    shouldApply: () => boolean = () => true
  ): Promise<void> => {
    const me = await userApi<MeResponse>('/api/users/me');
    const role = mapRole(me.role);

    if (!role) {
      clearToken();
      if (shouldApply()) {
        setCurrentUser(null);
        setUserRole(null);
        setAuthError('Unsupported account type.');
      }
      throw new Error('Unsupported account type.');
    }

    if (shouldApply()) {
      setCurrentUser(me);
      setUserRole(role);
    }
  };

  const signInWithEmail = async (
    email: string,
    password: string
  ): Promise<void> => {
    setAuthStatus('Signing in...');
    setAuthError(null);

    try {
      // better-auth's sign-in/email response body includes the session token
      // directly (`{ token, user, ... }`), so read it from there rather than
      // depending on a CORS-exposed response header or a follow-up
      // get-session call, either of which can be blocked by browser privacy
      // settings (e.g. third-party-cookie blocking).
      const result = await userApi<SignInResponse>('/api/auth/sign-in/email', {
        method: 'POST',
        body: { email, password },
        // A wrong-password sign-in returns 401, but that is a credential
        // failure, not session expiry. Suppress the global unauthorized
        // handler so the "session expired" banner never flashes before the
        // catch below surfaces the real "Invalid email or password." message.
        // Same rationale as the reset-password call (ResetPassword.js).
        suppress401Handler: true,
      });

      if (result?.token) {
        setToken(result.token);
      } else {
        // Last resort: the body should always include the token, but fall
        // back to the session endpoint if it's ever missing.
        await refreshTokenFromSession();
      }

      await loadCurrentUser();
    } catch (error) {
      const message =
        error instanceof ApiError &&
        (error.status === 401 || error.status === 400)
          ? 'Invalid email or password.'
          : (error instanceof Error && error.message) ||
            'Failed to sign in. Please check your credentials.';

      logError(error, 'client_auth', 'Email/password sign-in');
      setAuthError(message);
      setAuthStatus('Sign-in failed');
      throw error;
    }
  };

  const sendPasswordReset = async (email: string): Promise<boolean> => {
    try {
      await userApi('/api/auth/request-password-reset', {
        method: 'POST',
        body: { email },
      });
      return true;
    } catch (error) {
      logError(error, 'client_auth', 'Password reset');
      throw error;
    }
  };

  const updatePassword = async (
    newPassword: string,
    currentPassword: string | null = null
  ): Promise<boolean> => {
    try {
      // better-auth's change-password response body includes the new session
      // token directly (`{ token, user }`) whenever revokeOtherSessions is
      // true (which it always is here — see the request body below), so read
      // it from there rather than a follow-up get-session call. Same
      // rationale as signInWithEmail above: avoids depending on the cookie
      // round-trip.
      const result = await userApi<ChangePasswordResponse>(
        CHANGE_PASSWORD_PATH,
        {
          method: 'POST',
          body: {
            currentPassword,
            newPassword,
            revokeOtherSessions: true,
          },
        }
      );

      if (result?.token) {
        setToken(result.token);
      }

      return true;
    } catch (error) {
      logError(error, 'client_auth', 'Password update');
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await userApi('/api/auth/sign-out', {
        method: 'POST',
        suppress401Handler: true,
      });
    } catch {
      // Ignore errors — we clear local auth state regardless.
    }
    clearToken();
    setCurrentUser(null);
    setUserRole(null);
  };

  useEffect(() => {
    onUnauthorized(() => {
      setCurrentUser(null);
      setUserRole(null);
      setAuthError('Your session has expired. Please sign in again.');
    });

    const bootstrap = async (): Promise<void> => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const timeout = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new BootstrapTimeoutError('Session check timed out.')),
          BOOTSTRAP_TIMEOUT_MS
        );
      });

      try {
        // Read the already-persisted session token (set by signInWithEmail,
        // survives reload via tokenStore's localStorage backing) rather than
        // re-fetching it via a cookie-based get-session call, which can
        // silently fail cross-origin (Safari ITP, Chrome's third-party-
        // cookie phaseout). loadCurrentUser below still validates the token
        // is actually accepted server-side (via the Bearer header apiFetch
        // attaches) and clears state on a 401.
        if (!getToken()) {
          clearToken();
          setCurrentUser(null);
          setUserRole(null);
          return;
        }

        setAuthStatus('Loading user profile...');
        await Promise.race([
          loadCurrentUser(() => !bootstrapTimedOutRef.current),
          timeout,
        ]);
      } catch (error) {
        if (error instanceof BootstrapTimeoutError) {
          bootstrapTimedOutRef.current = true;
          logError(error, 'client_auth', 'Session bootstrap timed out');
          setAuthError(
            'Could not verify your session. Please check your connection and refresh the page.'
          );
          clearToken();
          setCurrentUser(null);
          setUserRole(null);
        } else if (error instanceof ApiError && error.status === 401) {
          clearToken();
          setCurrentUser(null);
          setUserRole(null);
          setAuthError(null);
        } else {
          logError(error, 'client_auth', 'Session bootstrap');
          clearToken();
          setCurrentUser(null);
          setUserRole(null);
        }
      } finally {
        clearTimeout(timeoutId);
        setLoading(false);
      }
    };

    bootstrap();
    // This effect should only run once to set up the session bootstrap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AuthContextValue = {
    currentUser,
    userRole,
    signInWithEmail,
    sendPasswordReset,
    updatePassword,
    logout,
    loading,
    authStatus,
    authError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
